import { IFileDto, IReferenceDto } from '../fileConfig'
import { FilePartyRole } from '../filePartyConfig'
import { MoneyDto } from '../i18nConfig'
import { DisbursementListItemDto, FixedPriceListItemDto, TimeListItemDto, TimeOverviewDto, TravelListItemDto } from '../registrationConfig'
import { PriceDto, PricePrecedence, PriceSummaryDto, QuantityDto, SurchargeListItemDto } from './financeConfig'
import { PartyStatus, PartyType } from '../../components/Form/Party/PartyForm'
import { DimensionsDto } from '../../components/Form/Accounting/DimensionFields'
import { LineItemEntity } from '../../hooks/finance/invoice/useLineItemEntities'
import { AttributeEntityDto } from '../../hooks/useAttributes'
import { ShortUserListItemDto } from '../userConfig'
import { PartyRecipientDto } from '../partyConfig'

export enum InvoiceType {
    Regular = 'REGULAR',
    Deposit = 'DEPOSIT',
    Refund = 'REFUND',
    Split = 'SPLIT',
    SubSplit = 'SUB_SPLIT',
}

export enum InvoiceStatus {
    New = 'NEW',
    Draft = 'DRAFT',
    Final = 'FINAL',
    Done = 'DONE',
    Settled = 'SETTLED',
    Credited = 'CREDITED', //Previously know as CANCELLED.
    SettledDone = 'SETTLED_DONE',
    CreditedDone = 'CREDITED_DONE',
}

export enum LineItemType {
    Time = 'TIME',
    Travel = 'TRAVEL',
    Disbursement = 'DISBURSEMENT',
    FixedPrice = 'FIXED_PRICE',
    Surcharge = 'SURCHARGE',
    Invoice = 'INVOICE',
}

export enum PrefillPeriod {
    Year = 'YEAR',
    Month = 'MONTH',
    Week = 'WEEK',
}

export enum PrefillDeterminer {
    Previous = 'PREVIOUS',
    CurrentIfMost = 'CURRENT_IF_MOST',
    Current = 'CURRENT',
    All = 'ALL',
}

export type InvoiceMessage = {
    language: string
    currency?: string
    subject: string
    body: string
}

export enum ConsolidationType {
    NONE = 'NONE',
    DIMENSIONS = 'DIMENSIONS',
    PERIOD = 'PERIOD',
    BOTH = 'BOTH',
}

export enum PaymentLinkStatus {
    OPEN = 'OPEN',
    PAID = 'PAID',
    EXPIRED = 'EXPIRED',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
}

type BaseLineItemGroup = {
    description: string
    showStatement: boolean
    consolidation: ConsolidationType
    vatType: string
    price: string
    order: number
    precedence: PricePrecedence
    accounting?: DimensionsDto
}

export type LineItemGroupMutationDto = Partial<BaseLineItemGroup> & {
    id?: string
    lineItems: LineItemMutationDto[]
    fileId: string
    fixedPriceId: string

    isNew?: boolean //internal use only
    templateGroupId?: string //internal use only
    file?: IReferenceDto //internal use only
}

export type LineItemGroupType = Partial<BaseLineItemGroup> & {
    id: string
    prefillType?: LineItemType
    prefillPeriod?: PrefillPeriod
    prefillOffset?: PrefillDeterminer
    prefillCutoff?: PrefillDeterminer
}

export type LineItemGroupDto = Partial<BaseLineItemGroup> & {
    id: string
    file: IReferenceDto
    fixedPrice: FixedPriceListItemDto
    price: PriceSummaryDto
    lineItems: LineItemDto[]
}

export type LineItemDto = {
    id: string
    type: LineItemType
    entityId?: string
    quantity: QuantityDto
    price: PriceDto
}

export type LineItemMutationDto = {
    id?: string
    type: LineItemType
    entityId?: string
}

export type BankTransactionDto = {
    id?: string
    value: string
    description: string
    date: string
}

export type PaymentLinkDto = {
    id: string
    externalId: string
    href: string
    currency: string
    value: string
    status: PaymentLinkStatus
    description: string
    expiresAt?: string
}

export type InvoiceListItemDtoAbstract = LineItemEntity & {
    type: InvoiceType
    number: string
    name: string
    date: string
    price: PriceDto
    outstandingPrice?: PriceDto
    unsettledPrice?: PriceDto
}

export type AccountingId = {
    id: string
    code: string
    name: string
    unit: string
}

export type AccountingIdEntity = AccountingId & {
    entityId: string
}

export type InvoiceOverviewDtoAbstract = InvoiceListItemDtoAbstract & {
    type: InvoiceType
    template: InvoiceOverviewTemplateDto
    parent?: InvoiceSummaryDto
    status: InvoiceStatus
    term: number
    recipient: RecipientListItemDto
    files: IReferenceDto[]
    language: string
    pdfId?: string
    credit?: InvoiceSummaryDto[]
    unit?: string | null
    transaction?: AccountingId
    accounting?: DimensionsDto
    factor?: number
    note?: string
}

export type InvoiceListItemDto = InvoiceListItemDtoAbstract & {
    filePartyId: string
}

export type InvoiceOverviewDto = InvoiceOverviewDtoAbstract & {
    paid: MoneyDto
    fileOwner: ShortUserListItemDto
}

export type InvoiceDto = InvoiceOverviewDtoAbstract & {
    organisationProfileKey?: string
    description: string
    message: InvoiceMessage
    paymentMessage: InvoiceMessage
    quantities: QuantityDto[]
    lineItemGroups: LineItemGroupDto[]
    glaDebitNumber?: string
    period?: string
    bankTransactions: BankTransactionDto[]
    paymentLink: PaymentLinkDto
    children: InvoiceDto[]
    paymentPrice?: MoneyDto
    invoiceApprovalId?: string
}

export type GlaDto = {
    debit: AccountingId
    revenues: AccountingIdEntity[]
    unit: string
}

export type MetaDataEntities = {
    files: FileMetaDataEntities[]
}

export type FileMetaDataEntities = {
    file: IFileDto
    fileParties: FilePartyMetaDataDto[]
    disbursements: DisbursementListItemDto[]
    fixedPrices: FixedPriceListItemDto[]
    invoices: InvoiceListItemDto[]
    surcharges: SurchargeListItemDto[]
    times: TimeListItemDto[]
    travels: TravelListItemDto[]
}

export type MetaDataDto = {
    number: string
    emailMessages: InvoiceMessage[]
    paymentMessages: InvoiceMessage[]
    pdfId?: string
    glas: GlaDto[]
} & MetaDataEntities

type BaseInvoice<LineItemGroupT> = {
    type: InvoiceType
    organisationProfileKey?: string
    name: string
    description: string
    term: number
    unit?: string
    language: string
    accounting?: DimensionsDto
    lineItemGroups: LineItemGroupT[]
}

export type InvoiceOverviewTemplateDto = {
    id: string
    name: string
}

export type InvoiceTemplateDto = BaseInvoice<LineItemGroupType> & {
    id: string
    enabled?: boolean
    isDefault?: boolean
}

export type InvoiceMutationDto = BaseInvoice<LineItemGroupMutationDto> &
    AttributeEntityDto & {
        id?: string
        templateId: string
        invoiceApprovalId?: string
        parentId?: string
        transaction?: AccountingId
        children?: InvoiceMutationDto[]
        factor?: number
        fileIds: string[]
        status: InvoiceStatus
        number: string
        date: string
        period?: string
        clientPivotId?: string
        debtorPivotId?: string
        currency: string
        paymentPrice?: string
        recipientReference?: string
        recipientReferenceRequired?: boolean //used locally only
        recipientEmail?: string
        messageSubject: string
        messageBody: string
        paymentMessageBody?: string
        note?: string
    }

export type InvoiceSummaryDto = {
    id: string
    number: string
    status: InvoiceStatus
    pdfId: string
}

export type FilePartyMetaDataDto = PartyRecipientDto & {
    id: string
    pivotId: string
    status: PartyStatus
    name: string
    email: string
    phoneNumber: string
    displayName: string
    role: FilePartyRole | string
    type: PartyType
    term: number
    externals: AccountingId[]
    financeEmail?: string
    referenceRequired?: boolean
    reference?: string //The combined value of reference and fileReference of a Party/FileParty.
}

export type RecipientListItemDto = {
    id: string
    clientId: string
    debtorId: string
    name: string
    email: string
    reference: string
    referenceRequired?: boolean
}

export interface ITimeItemDto extends TimeOverviewDto {
    timeId: string
}

export const InvoiceTypeMap: Map<InvoiceType, string> = new Map<InvoiceType, string>([
    [InvoiceType.Regular, 'Regulier'],
    [InvoiceType.Deposit, 'Voorschot'],
    [InvoiceType.Refund, 'Credit'],
    [InvoiceType.Split, 'Gesplitste declaratie'],
    [InvoiceType.SubSplit, 'Subdeclaratie'],
])

export const InvoiceFinalList = [
    InvoiceStatus.Final,
    InvoiceStatus.Done,
    InvoiceStatus.Settled,
    InvoiceStatus.Credited,
    InvoiceStatus.SettledDone,
    InvoiceStatus.CreditedDone,
]
